import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import { categoryLabel } from '../../lib/format.js';

interface LibDoc {
  id: string;
  originalName?: string;
  category?: string;
  docType?: string;
  filingEntity?: string;
  year?: number;
  status?: string;
  uploadedAt?: { seconds: number };
}

const UNASSIGNED = 'Unassigned';

interface CategoryNode {
  category: string;
  label: string;
  docs: LibDoc[];
}

interface EntityNode {
  entity: string;
  count: number;
  categories: CategoryNode[];
}

// categoryLabel can throw on malformed keys; fall back to a naive titlecase.
function safeLabel(key: string): string {
  try {
    return categoryLabel(key);
  } catch {
    return key
      .split('_')
      .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p))
      .join(' ');
  }
}

function entityOf(d: LibDoc): string {
  const e = (d.filingEntity ?? '').trim();
  return e.length ? e : UNASSIGNED;
}

function formatUploaded(ts: { seconds: number } | undefined): string {
  if (!ts) return '—';
  return new Date(ts.seconds * 1000).toLocaleDateString();
}

function statusColor(status: string | undefined): string {
  if (status === 'ready') return 'bg-emerald-100 text-emerald-800';
  if (status === 'triage') return 'bg-orange-100 text-orange-800';
  if (status === 'error') return 'bg-[#9C4A3A]/15 text-[#9C4A3A]';
  return 'bg-stone-100 text-stone-700';
}

function StatusPill({ status }: { status: string | undefined }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(status)}`}
    >
      {status ?? 'ready'}
    </span>
  );
}

export function DocumentLibrary() {
  const { role } = useAuth();
  const [docs, setDocs] = useState<LibDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openEntities, setOpenEntities] = useState<Record<string, boolean>>({});
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Some docs lack filingEntity, so an orderBy on it would silently drop them —
  // pull the collection unordered and sort in memory instead.
  useEffect(() => {
    if (role !== 'admin') return;
    const q = query(collection(db, 'documents'), limit(2000));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<LibDoc, 'id'>) })));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [role]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return docs;
    return docs.filter((d) => {
      const hay = [
        d.filingEntity ?? '',
        d.originalName ?? '',
        d.category ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(term);
    });
  }, [docs, search]);

  const tree = useMemo<EntityNode[]>(() => {
    const byEntity = new Map<string, Map<string, LibDoc[]>>();
    for (const d of filtered) {
      const ent = entityOf(d);
      const cat = d.category ?? 'other';
      let cats = byEntity.get(ent);
      if (!cats) {
        cats = new Map<string, LibDoc[]>();
        byEntity.set(ent, cats);
      }
      const arr = cats.get(cat);
      if (arr) arr.push(d);
      else cats.set(cat, [d]);
    }
    const nodes: EntityNode[] = [];
    for (const [entity, cats] of byEntity) {
      const categories: CategoryNode[] = [];
      let count = 0;
      for (const [category, list] of cats) {
        count += list.length;
        categories.push({ category, label: safeLabel(category), docs: list });
      }
      categories.sort((a, b) => a.label.localeCompare(b.label));
      nodes.push({ entity, count, categories });
    }
    // Alphabetical, but Unassigned always sinks to the bottom.
    nodes.sort((a, b) => {
      if (a.entity === UNASSIGNED) return 1;
      if (b.entity === UNASSIGNED) return -1;
      return a.entity.localeCompare(b.entity);
    });
    return nodes;
  }, [filtered]);

  if (role !== 'admin') {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
        <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-stone-600">Admins only.</p>
        </div>
      </div>
    );
  }

  const totalDocs = filtered.length;
  const totalEntities = tree.length;

  const activeEntity = selectedEntity
    ? tree.find((n) => n.entity === selectedEntity) ?? null
    : null;
  const activeCategory =
    activeEntity && selectedCategory
      ? activeEntity.categories.find((c) => c.category === selectedCategory) ?? null
      : null;

  function selectCategory(entity: string, category: string) {
    setSelectedEntity(entity);
    setSelectedCategory(category);
  }

  function drillEntity(entity: string) {
    setSelectedEntity(entity);
    setSelectedCategory(null);
    setOpenEntities((prev) => ({ ...prev, [entity]: true }));
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/documents" className="text-xs text-stone-500 hover:underline">
            ← Documents
          </Link>
          <h1 className="mt-2 text-2xl font-bold tracking-tight">Document library</h1>
          <p className="text-sm text-stone-500">
            Every classified document, filed by entity then type.
          </p>
        </div>
        <div className="text-right text-xs text-stone-500">
          <div>
            {totalDocs} {totalDocs === 1 ? 'document' : 'documents'}
          </div>
          <div>
            {totalEntities} {totalEntities === 1 ? 'entity' : 'entities'}
          </div>
        </div>
      </header>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search entities, documents, types…"
        className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-stone-400"
      />

      {loading ? (
        <div className="rounded-2xl border border-stone-200 bg-white p-5 text-center text-sm text-stone-400 shadow-sm">
          Loading…
        </div>
      ) : (
        <div className="flex flex-col gap-6 md:flex-row">
          <aside className="w-full md:w-72 md:flex-shrink-0">
            <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
              <div className="text-[10px] uppercase tracking-wider text-stone-400">Entities</div>
              <div className="mt-2 space-y-1">
                {tree.length === 0 && (
                  <div className="py-4 text-center text-xs text-stone-400">No documents.</div>
                )}
                {tree.map((node) => {
                  const open = openEntities[node.entity] ?? false;
                  return (
                    <div key={node.entity}>
                      <button
                        type="button"
                        onClick={() => {
                          setOpenEntities((prev) => ({ ...prev, [node.entity]: !open }));
                          drillEntity(node.entity);
                        }}
                        className={`flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm ${
                          selectedEntity === node.entity
                            ? 'bg-stone-100 text-stone-900'
                            : 'text-stone-700 hover:bg-stone-50'
                        }`}
                      >
                        <span className="flex items-center gap-1.5 truncate">
                          <span className="text-stone-400">{open ? '▾' : '▸'}</span>
                          <span className="truncate">{node.entity}</span>
                        </span>
                        <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-600">
                          {node.count}
                        </span>
                      </button>
                      {open && (
                        <div className="ml-5 mt-0.5 space-y-0.5 border-l border-stone-100 pl-2">
                          {node.categories.map((cat) => (
                            <button
                              key={cat.category}
                              type="button"
                              onClick={() => selectCategory(node.entity, cat.category)}
                              className={`flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-xs ${
                                selectedEntity === node.entity &&
                                selectedCategory === cat.category
                                  ? 'bg-stone-900 text-stone-50'
                                  : 'text-stone-600 hover:bg-stone-50'
                              }`}
                            >
                              <span className="truncate">{cat.label}</span>
                              <span className="ml-2 text-stone-400">{cat.docs.length}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          <section className="min-w-0 flex-1">
            <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
              <nav className="mb-4 flex flex-wrap items-center gap-1 text-xs text-stone-500">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedEntity(null);
                    setSelectedCategory(null);
                  }}
                  className="hover:underline"
                >
                  All
                </button>
                {activeEntity && (
                  <>
                    <span className="text-stone-300">/</span>
                    <button
                      type="button"
                      onClick={() => drillEntity(activeEntity.entity)}
                      className="hover:underline"
                    >
                      {activeEntity.entity}
                    </button>
                  </>
                )}
                {activeCategory && (
                  <>
                    <span className="text-stone-300">/</span>
                    <span className="text-stone-700">{activeCategory.label}</span>
                  </>
                )}
              </nav>

              {activeCategory ? (
                <DocTable docs={activeCategory.docs} />
              ) : activeEntity ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {activeEntity.categories.map((cat) => (
                    <button
                      key={cat.category}
                      type="button"
                      onClick={() => selectCategory(activeEntity.entity, cat.category)}
                      className="flex items-center justify-between rounded-xl border border-stone-200 px-3 py-3 text-left text-sm hover:bg-stone-50"
                    >
                      <span className="truncate font-medium text-stone-800">{cat.label}</span>
                      <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-600">
                        {cat.docs.length}
                      </span>
                    </button>
                  ))}
                </div>
              ) : tree.length === 0 ? (
                <div className="py-10 text-center text-sm text-stone-400">No documents.</div>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {tree.map((node) => (
                    <button
                      key={node.entity}
                      type="button"
                      onClick={() => drillEntity(node.entity)}
                      className="flex items-center justify-between rounded-xl border border-stone-200 px-3 py-3 text-left text-sm hover:bg-stone-50"
                    >
                      <span className="truncate font-medium text-stone-800">{node.entity}</span>
                      <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-600">
                        {node.count}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function DocTable({ docs }: { docs: LibDoc[] }) {
  if (docs.length === 0) {
    return <div className="py-10 text-center text-sm text-stone-400">No documents here.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-stone-200 text-left text-xs uppercase tracking-wider text-stone-400">
          <tr>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Year</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Uploaded</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} className="border-b border-stone-100 hover:bg-stone-50">
              <td className="px-3 py-2">
                <Link to={`/documents/${d.id}`} className="font-medium text-stone-900">
                  {d.originalName ?? d.id}
                </Link>
              </td>
              <td className="px-3 py-2 text-stone-600">{d.docType ?? '—'}</td>
              <td className="px-3 py-2 text-stone-500">{d.year ?? '—'}</td>
              <td className="px-3 py-2">
                <StatusPill status={d.status} />
              </td>
              <td className="px-3 py-2 text-xs text-stone-500">{formatUploaded(d.uploadedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
